import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Star as StarIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useRulesStore } from '../../stores/rulesStore';
import type { AutomationProfile } from '../../types';

type DialogMode = 'create' | 'edit' | null;

export const AutomationProfiles: React.FC = () => {
  const {
    automationProfiles,
    addAutomationProfile,
    updateAutomationProfile,
    deleteAutomationProfile,
    setDefaultAutomationProfile,
    toggleAutomationProfileActive,
  } = useRulesStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedProfile, setSelectedProfile] = useState<AutomationProfile | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'balanced' as AutomationProfile['type'],
    hardRuleEnforcement: 'always' as AutomationProfile['hardRuleEnforcement'],
    softRuleWeight: 5,
    preferenceWeight: 5,
    teacherPreferenceWeight: 5,
    maxGenerationTime: 120,
    conflictTolerance: 'minimal' as AutomationProfile['conflictTolerance'],
    prioritizeTeacherWellbeing: true,
    prioritizeStudentWellbeing: true,
    prioritizeRoomEfficiency: false,
    allowSoftRuleViolations: true,
    maxSoftRuleViolations: 10,
  });

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      type: 'balanced',
      hardRuleEnforcement: 'always',
      softRuleWeight: 5,
      preferenceWeight: 5,
      teacherPreferenceWeight: 5,
      maxGenerationTime: 120,
      conflictTolerance: 'minimal',
      prioritizeTeacherWellbeing: true,
      prioritizeStudentWellbeing: true,
      prioritizeRoomEfficiency: false,
      allowSoftRuleViolations: true,
      maxSoftRuleViolations: 10,
    });
    setSelectedProfile(null);
    setDialogMode('create');
  };

  const handleEdit = (profile: AutomationProfile) => {
    setFormData({
      name: profile.name,
      description: profile.description,
      type: profile.type,
      hardRuleEnforcement: profile.hardRuleEnforcement,
      softRuleWeight: profile.softRuleWeight,
      preferenceWeight: profile.preferenceWeight,
      teacherPreferenceWeight: profile.teacherPreferenceWeight,
      maxGenerationTime: profile.maxGenerationTime,
      conflictTolerance: profile.conflictTolerance,
      prioritizeTeacherWellbeing: profile.settings.prioritizeTeacherWellbeing,
      prioritizeStudentWellbeing: profile.settings.prioritizeStudentWellbeing,
      prioritizeRoomEfficiency: profile.settings.prioritizeRoomEfficiency,
      allowSoftRuleViolations: profile.settings.allowSoftRuleViolations,
      maxSoftRuleViolations: profile.settings.maxSoftRuleViolations,
    });
    setSelectedProfile(profile);
    setDialogMode('edit');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      deleteAutomationProfile(id);
    }
  };

  const handleSetDefault = (id: string) => {
    setDefaultAutomationProfile(id);
  };

  const handleToggleActive = (id: string) => {
    toggleAutomationProfileActive(id);
  };

  const handleSubmit = () => {
    const newProfile: AutomationProfile = {
      id: selectedProfile?.id || `profile-${Date.now()}`,
      name: formData.name,
      description: formData.description,
      type: formData.type,
      hardRuleEnforcement: formData.hardRuleEnforcement,
      softRuleWeight: formData.softRuleWeight,
      preferenceWeight: formData.preferenceWeight,
      teacherPreferenceWeight: formData.teacherPreferenceWeight,
      maxGenerationTime: formData.maxGenerationTime,
      conflictTolerance: formData.conflictTolerance,
      isDefault: selectedProfile?.isDefault || false,
      isActive: selectedProfile?.isActive ?? true,
      settings: {
        prioritizeTeacherWellbeing: formData.prioritizeTeacherWellbeing,
        prioritizeStudentWellbeing: formData.prioritizeStudentWellbeing,
        prioritizeRoomEfficiency: formData.prioritizeRoomEfficiency,
        allowSoftRuleViolations: formData.allowSoftRuleViolations,
        maxSoftRuleViolations: formData.maxSoftRuleViolations,
      },
      createdAt: selectedProfile?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    if (dialogMode === 'create') {
      addAutomationProfile(newProfile);
    } else if (dialogMode === 'edit' && selectedProfile) {
      updateAutomationProfile(selectedProfile.id, newProfile);
    }
    setDialogMode(null);
    setSelectedProfile(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedProfile(null);
  };

  const getTypeColor = (type: AutomationProfile['type']) => {
    const colors: Record<AutomationProfile['type'], 'error' | 'success' | 'warning'> = {
      strict: 'error',
      balanced: 'success',
      flexible: 'warning',
    };
    return colors[type];
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Automation Profiles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Configure rule weighting strategies for different generation scenarios
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Profile
          </Button>
        </Box>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Total Profiles
              </Typography>
              <Typography variant="h3">{automationProfiles.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Active Profiles
              </Typography>
              <Typography variant="h3">{automationProfiles.filter((p) => p.isActive).length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Default Profile
              </Typography>
              <Typography variant="h6">{automationProfiles.find((p) => p.isDefault)?.name || 'None'}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Profiles Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Weights (Soft/Pref/Teacher)</TableCell>
                <TableCell>Max Time</TableCell>
                <TableCell>Conflict Tolerance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {automationProfiles.map((profile) => (
                <TableRow key={profile.id} sx={{ bgcolor: profile.isDefault ? '#f0f7ff' : 'inherit' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {profile.isDefault && <StarIcon color="primary" fontSize="small" />}
                      <Box>
                        <Typography variant="subtitle2">{profile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {profile.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={profile.type} color={getTypeColor(profile.type)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {profile.softRuleWeight} / {profile.preferenceWeight} / {profile.teacherPreferenceWeight}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{profile.maxGenerationTime}s</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={profile.conflictTolerance} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={profile.isActive ? 'Active' : 'Inactive'}
                      color={profile.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(profile.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleSetDefault(profile.id)}
                      color={profile.isDefault ? 'primary' : 'default'}
                    >
                      <StarIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(profile)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(profile.id)}
                      disabled={profile.isDefault}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogMode !== null} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Create New Profile' : 'Edit Profile'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Profile Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as AutomationProfile['type'] })}
                    fullWidth
                    SelectProps={{ native: true }}
                  >
                    <option value="strict">Strict</option>
                    <option value="balanced">Balanced</option>
                    <option value="flexible">Flexible</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Conflict Tolerance"
                    value={formData.conflictTolerance}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        conflictTolerance: e.target.value as AutomationProfile['conflictTolerance'],
                      })
                    }
                    fullWidth
                    SelectProps={{ native: true }}
                  >
                    <option value="none">None</option>
                    <option value="minimal">Minimal</option>
                    <option value="moderate">Moderate</option>
                    <option value="high">High</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Soft Rule Weight: {formData.softRuleWeight}</Typography>
                  <Slider
                    value={formData.softRuleWeight}
                    onChange={(_, v) => setFormData({ ...formData, softRuleWeight: v as number })}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Preference Weight: {formData.preferenceWeight}</Typography>
                  <Slider
                    value={formData.preferenceWeight}
                    onChange={(_, v) => setFormData({ ...formData, preferenceWeight: v as number })}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Teacher Preference Weight: {formData.teacherPreferenceWeight}</Typography>
                  <Slider
                    value={formData.teacherPreferenceWeight}
                    onChange={(_, v) => setFormData({ ...formData, teacherPreferenceWeight: v as number })}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Max Generation Time (seconds)"
                    type="number"
                    value={formData.maxGenerationTime}
                    onChange={(e) => setFormData({ ...formData, maxGenerationTime: Number(e.target.value) })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Prioritize Teacher Wellbeing</Typography>
                    <Switch
                      checked={formData.prioritizeTeacherWellbeing}
                      onChange={(e) => setFormData({ ...formData, prioritizeTeacherWellbeing: e.target.checked })}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Prioritize Student Wellbeing</Typography>
                    <Switch
                      checked={formData.prioritizeStudentWellbeing}
                      onChange={(e) => setFormData({ ...formData, prioritizeStudentWellbeing: e.target.checked })}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>Prioritize Room Efficiency</Typography>
                    <Switch
                      checked={formData.prioritizeRoomEfficiency}
                      onChange={(e) => setFormData({ ...formData, prioritizeRoomEfficiency: e.target.checked })}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AutomationProfiles;
